'use client'

import { textVariants } from '#components/text/styles'
import { TextColor, TextSize, TextWeight } from '#components/text/types'
import { cn } from '#lib/cn'

import {
  inputContainerVariants,
  inputPaddingClasses,
  inputSlotVariants,
} from './styles'
import { type InputContainerProps, InputSize, InputSlotPosition } from './types'

const slotTextClasses = cn(
  textVariants({
    color: TextColor.Secondary,
    size: TextSize.Small,
    weight: TextWeight.Strong,
  }),
  'select-none',
)

/** Returns true if the click target is not an interactive element.
 *  Assumes the container only holds `<input>`, `<button>`, and `<a>` as interactive children. */
function isDeadArea(target: HTMLElement) {
  return (
    target.tagName !== 'INPUT' &&
    target.tagName !== 'BUTTON' &&
    target.tagName !== 'A' &&
    !target.closest('button, a, [role="button"]')
  )
}

function getDeadAreaInput(
  e: { target: EventTarget; currentTarget: HTMLDivElement },
  disabled: boolean,
): HTMLInputElement | null {
  if (disabled) {
    return null
  }
  if (!isDeadArea(e.target as HTMLElement)) {
    return null
  }
  return e.currentTarget.querySelector('input')
}

/**
 * InputContainer - A styled container that provides input-like appearance.
 *
 * Used internally by BaseInput, but can also be used directly for
 * custom input-like components (e.g., select trigger, combobox).
 *
 * @example
 * ```tsx
 * <InputContainer
 *   size={InputSize.Medium}
 *   invalid={hasError}
 *   rightSection={<IconChevronDown />}
 * >
 *   <span>Selected value</span>
 * </InputContainer>
 * ```
 */
export function InputContainer({
  className,
  size = InputSize.Medium,
  invalid = false,
  disabled = false,
  leftSection,
  rightSection,
  children,
  ref,
  onDeadAreaPointerDown,
  onDeadAreaClick,
}: InputContainerProps) {
  const padding = inputPaddingClasses[size]

  return (
    // biome-ignore lint/a11y/useKeyWithClickEvents: Click-to-focus behavior, keyboard handled by the input itself
    // biome-ignore lint/a11y/noStaticElementInteractions: Mimics label click-to-focus, not a true interactive element
    <div
      className={cn(
        inputContainerVariants({ size }),
        !leftSection && padding.left,
        !rightSection && padding.right,
        className,
      )}
      data-disabled={disabled ? true : undefined}
      data-invalid={invalid ? true : undefined}
      ref={ref}
      onPointerDown={(e) => {
        if (!onDeadAreaPointerDown) {
          return
        }
        const input = getDeadAreaInput(e, disabled)
        if (input) {
          onDeadAreaPointerDown(input, e)
        }
      }}
      onClick={(e) => {
        const input = getDeadAreaInput(e, disabled)
        if (input) {
          input.focus()
          return
        }
        // No inner input (e.g. button-trigger Select): hand dead-area clicks
        // to the consumer so it can activate its own trigger.
        if (
          onDeadAreaClick &&
          !disabled &&
          isDeadArea(e.target as HTMLElement)
        ) {
          onDeadAreaClick(e)
        }
      }}
    >
      {leftSection && (
        <span
          className={cn(
            inputSlotVariants({ size, position: InputSlotPosition.Left }),
            slotTextClasses,
          )}
        >
          {leftSection}
        </span>
      )}
      {children}
      {rightSection && (
        <span
          className={cn(
            inputSlotVariants({ size, position: InputSlotPosition.Right }),
            slotTextClasses,
          )}
        >
          {rightSection}
        </span>
      )}
    </div>
  )
}

InputContainer.displayName = 'InputContainer'
